import React, { useEffect, useState } from 'react';
import {
  BarChart3,
  Banknote,
  PieChart,
  Users,
  UserCheck,
  FileDown,
  FileText,
  SlidersHorizontal,
  RotateCcw,
  Package,
  Loader,
  CheckCircle2,
  XCircle,
  Hourglass,
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { adminApi } from '../../api/client';
import { BarChart, HBarChart, DonutChart } from '../../components/dashboard/AdminCharts';
import './AdminHome.css';
import './AdminOrders.css';

const DAY = 24 * 60 * 60 * 1000;

const DONE_ST = ['delivered', 'completed'];
const CANCEL_ST = ['cancelled', 'refused', 'payment_failed', 'quote_expired'];
const PENDING_ST = [
  'draft', 'pending', 'request_sent', 'pending_review', 'quote_sent',
  'quote_accepted', 'payment_pending', 'paid', 'translator_to_assign', 'translator_unavailable',
];

const orderBucket = (o) => {
  if (DONE_ST.includes(o.status)) return 'done';
  if (CANCEL_ST.includes(o.status)) return 'cancelled';
  if (PENDING_ST.includes(o.status) && !o.translatorId) return 'pending';
  return 'inProgress';
};

export default function AdminReportsPage() {
  const { t } = useLanguage();
  const a = t.dashboard.admin;
  const d = t.dashboard;

  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [clients, setClients] = useState([]);
  const [translators, setTranslators] = useState([]);

  const [period, setPeriod] = useState('month');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [category, setCategory] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [translatorFilter, setTranslatorFilter] = useState('all');

  useEffect(() => {
    adminApi.orders().then((r) => setOrders(r.orders || [])).catch(() => setOrders([]));
    adminApi.payments().then((r) => setPayments(r.payments || [])).catch(() => setPayments([]));
    adminApi.users().then((r) => setClients((r.users || []).filter((u) => u.role === 'client'))).catch(() => setClients([]));
    adminApi.translators().then((r) => setTranslators(r.translators || [])).catch(() => setTranslators([]));
  }, []);

  const now = new Date();
  const range = (() => {
    const day = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    switch (period) {
      case 'today':
        return { from: day, to: now };
      case 'week':
        return { from: new Date(day.getTime() - 6 * DAY), to: now };
      case 'month':
        return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: now };
      case 'quarter': {
        const q = Math.floor(now.getMonth() / 3) * 3;
        return { from: new Date(now.getFullYear(), q, 1), to: now };
      }
      case 'year':
        return { from: new Date(now.getFullYear(), 0, 1), to: now };
      case 'custom':
        return {
          from: dateFrom ? new Date(`${dateFrom}T00:00:00`) : null,
          to: dateTo ? new Date(`${dateTo}T23:59:59`) : null,
        };
      default:
        return { from: null, to: null };
    }
  })();

  const inRange = (v) => {
    const dt = new Date(v);
    if (Number.isNaN(dt.getTime())) return false;
    if (range.from && dt < range.from) return false;
    if (range.to && dt > range.to) return false;
    return true;
  };

  const fOrders = orders.filter((o) => {
    if (!inRange(o.createdAt || o.date)) return false;
    if (category !== 'all' && o.category !== category) return false;
    if (translatorFilter !== 'all' && String(o.translatorId) !== translatorFilter) return false;
    if (statusFilter !== 'all' && orderBucket(o) !== statusFilter) return false;
    return true;
  });

  const fPayments = payments.filter((p) => inRange(p.paidAt || p.createdAt));

  const days = range.from && range.to ? Math.ceil((range.to - range.from) / DAY) : 0;
  const useMonths = days > 62 || (!range.from && !range.to);
  const keyOf = (v) => {
    const dt = new Date(v);
    const ym = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
    return useMonths ? ym : `${ym}-${String(dt.getDate()).padStart(2, '0')}`;
  };
  const labelOf = (key) => (useMonths ? `${key.slice(5, 7)}/${key.slice(0, 4)}` : `${key.slice(8, 10)}/${key.slice(5, 7)}`);

  const chartKeys = (() => {
    const keys = [];
    if (useMonths) {
      const end = range.to || now;
      let dt = range.from
        ? new Date(range.from.getFullYear(), range.from.getMonth(), 1)
        : new Date(end.getFullYear(), 0, 1);
      while (dt <= end) {
        keys.push(keyOf(dt));
        dt = new Date(dt.getFullYear(), dt.getMonth() + 1, 1);
      }
    } else {
      const end = range.to || now;
      let dt = range.from || new Date(end.getTime() - 29 * DAY);
      while (dt <= end) {
        keys.push(keyOf(dt));
        dt = new Date(dt.getTime() + DAY);
      }
    }
    return keys;
  })();

  const ordersByKey = fOrders.reduce((m, o) => {
    const k = keyOf(o.createdAt || o.date);
    m[k] = (m[k] || 0) + 1;
    return m;
  }, {});
  const ordersChart = chartKeys.map((k) => ({ key: k, label: labelOf(k), count: ordersByKey[k] || 0 }));

  const revenueByKey = fPayments.reduce((m, p) => {
    if (p.status !== 'paid') return m;
    const k = keyOf(p.paidAt || p.createdAt);
    m[k] = (m[k] || 0) + (Number(p.amount) || 0);
    return m;
  }, {});
  const revenueChart = chartKeys.map((k) => ({ key: k, label: labelOf(k), amount: revenueByKey[k] || 0 }));

  const sum = (rows) => rows.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const orderKpis = [
    { icon: Package, label: 'Commandes', value: fOrders.length },
    { icon: CheckCircle2, label: 'Terminées', value: fOrders.filter((o) => orderBucket(o) === 'done').length },
    { icon: Loader, label: 'En cours', value: fOrders.filter((o) => orderBucket(o) === 'inProgress').length },
    { icon: XCircle, label: 'Annulées', value: fOrders.filter((o) => orderBucket(o) === 'cancelled').length },
    { icon: Hourglass, label: "En attente d'affectation", value: fOrders.filter((o) => orderBucket(o) === 'pending').length },
  ];
  const financeKpis = [
    { icon: Banknote, label: "Chiffre d'affaires", value: `${sum(fPayments.filter((p) => p.status === 'paid'))} TND` },
    { icon: CheckCircle2, label: 'Montant payé', value: `${sum(fPayments.filter((p) => p.status === 'paid'))} TND` },
    { icon: Hourglass, label: 'En attente', value: `${sum(fPayments.filter((p) => p.status === 'pending'))} TND` },
    { icon: RotateCcw, label: 'Remboursé', value: `${sum(fPayments.filter((p) => p.status === 'refunded'))} TND` },
  ];

  const catCounts = fOrders.reduce((m, o) => {
    const k = o.category || 'translation';
    m[k] = (m[k] || 0) + 1;
    return m;
  }, {});
  const catChart = Object.entries(catCounts).map(([key, count]) => ({ key, count }));
  const catLabels = { translation: 'Traduction', interpreting: 'Interprétariat' };

  const activeTranslators = translators.filter((tr) => tr.isActive !== false);
  const missionsTotal = translators.reduce((s, tr) => s + (Number(tr.missions) || 0), 0);
  const assignedOrders = fOrders.filter((o) => o.translatorId);
  const trKpis = [
    { label: 'Professionnels actifs', value: activeTranslators.length },
    { label: 'Missions réalisées', value: missionsTotal },
    { label: 'Commandes affectées', value: assignedOrders.length },
    { label: 'Commandes terminées', value: assignedOrders.filter((o) => DONE_ST.includes(o.status)).length },
    { label: 'Commandes refusées', value: fOrders.filter((o) => o.status === 'refused').length },
    { label: 'Disponibles', value: activeTranslators.filter((tr) => (tr.availabilityStatus || tr.translatorProfile?.availabilityStatus || 'disponible') === 'disponible').length },
  ];

  const ordersByTranslator = assignedOrders.reduce((m, o) => {
    const name = o.translator || '—';
    m[name] = (m[name] || 0) + 1;
    return m;
  }, {});
  const topTranslators = Object.entries(ordersByTranslator)
    .map(([label, count]) => ({ label, count }))
    .sort((x, y) => y.count - x.count)
    .slice(0, 6);

  const newClients = clients.filter((c) => inRange(c.createdAt)).length;
  const activeClientIds = new Set(fOrders.map((o) => o.clientId).filter(Boolean));
  const clientKpis = [
    { label: 'Nouveaux clients', value: newClients },
    { label: 'Clients actifs', value: activeClientIds.size },
    { label: 'Commandes / client', value: activeClientIds.size ? (fOrders.length / activeClientIds.size).toFixed(1) : '0' },
  ];
  const ordersByClient = fOrders.reduce((m, o) => {
    const name = o.client || '—';
    m[name] = (m[name] || 0) + 1;
    return m;
  }, {});
  const topClients = Object.entries(ordersByClient)
    .map(([label, count]) => ({ label, count }))
    .sort((x, y) => y.count - x.count)
    .slice(0, 6);

  const exportCsv = () => {
    const header = ['Commande', 'Client', 'Prestation', 'Langues', 'Statut', 'Montant', 'Paiement', 'Date'];
    const rows = fOrders.map((o) => [
      o.id,
      o.client || '',
      o.category === 'interpreting' ? 'Interprétariat' : 'Traduction',
      o.langs || '',
      d.status?.[o.status] || o.status,
      o.total ?? o.price ?? '',
      d.payment?.[o.paymentStatus] || o.paymentStatus || '',
      o.date || '',
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rapport-commandes-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const resetFilters = () => {
    setPeriod('month');
    setDateFrom('');
    setDateTo('');
    setCategory('all');
    setStatusFilter('all');
    setTranslatorFilter('all');
  };

  return (
    <>
      <header className="dash-page-head dash-page-head--split">
        <div>
          <h1>{a.reportsTitle || 'Rapports'}</h1>
          <p>{a.reportsSubtitle || "Analyser l'activité, les commandes, les prestations et les revenus de Tarjemlek."}</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => window.print()}
            style={{ background: '#fff', borderColor: 'var(--teal)', color: 'var(--teal)' }}
          >
            <FileText size={15} /> Exporter PDF
          </button>
          <button type="button" className="btn btn-primary" onClick={exportCsv}>
            <FileDown size={15} /> Exporter Excel
          </button>
        </div>
      </header>

      <div className="orders-filters">
        <div className="orders-filters-head">
          <span className="of-title"><SlidersHorizontal size={13} />Filtres</span>
          <span className="of-meta">
            <button type="button" className="of-reset" onClick={resetFilters}>
              <RotateCcw size={13} />Réinitialiser
            </button>
          </span>
        </div>
        <div className="orders-filters-grid">
          <div className="of-field">
            <label>Période</label>
            <select value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="today">Aujourd'hui</option>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
              <option value="quarter">Ce trimestre</option>
              <option value="year">Cette année</option>
              <option value="custom">Personnalisée</option>
            </select>
          </div>
          {period === 'custom' && (
            <>
              <div className="of-field">
                <label>Du</label>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div className="of-field">
                <label>Au</label>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
            </>
          )}
          <div className="of-field">
            <label>Prestation</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="all">Toutes</option>
              <option value="translation">Traduction</option>
              <option value="interpreting">Interprétariat</option>
            </select>
          </div>
          <div className="of-field">
            <label>{d.statusLabel || 'Statut'}</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">Tous</option>
              <option value="pending">En attente d'affectation</option>
              <option value="inProgress">En cours</option>
              <option value="done">Terminées</option>
              <option value="cancelled">Annulées</option>
            </select>
          </div>
          <div className="of-field">
            <label>Professionnel</label>
            <select value={translatorFilter} onChange={(e) => setTranslatorFilter(e.target.value)}>
              <option value="all">Tous</option>
              {translators.map((tr) => (
                <option key={tr.id} value={tr.id}>{tr.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <section className="dash-panel" style={{ marginBottom: 16 }}>
        <h2><BarChart3 size={16} /> Rapport des commandes</h2>
        <div className="cp-stats" style={{ marginBottom: 14 }}>
          {orderKpis.map((k) => (
            <div className="dash-stat cp-stat" key={k.label}>
              <span className="cp-stat-icon"><k.icon size={15} /></span>
              <span>{k.label}</span>
              <strong>{k.value}</strong>
            </div>
          ))}
        </div>
        {ordersChart.length ? (
          <BarChart data={ordersChart} />
        ) : (
          <p className="dash-empty">{d.empty}</p>
        )}
      </section>

      <section className="dash-panel" style={{ marginBottom: 16 }}>
        <h2><Banknote size={16} /> Rapport financier</h2>
        <div className="cp-stats" style={{ marginBottom: 14 }}>
          {financeKpis.map((k) => (
            <div className="dash-stat cp-stat" key={k.label}>
              <span className="cp-stat-icon"><k.icon size={15} /></span>
              <span>{k.label}</span>
              <strong>{k.value}</strong>
            </div>
          ))}
        </div>
        {revenueChart.length ? (
          <BarChart data={revenueChart} valueKey="amount" format={(v) => `${v} TND`} accent="var(--gold)" />
        ) : (
          <p className="dash-empty">{d.empty}</p>
        )}
      </section>

      <div className="od-grid" style={{ marginBottom: 16 }}>
        <section className="dash-panel">
          <h2><PieChart size={16} /> Rapport par prestation</h2>
          {catChart.length ? (
            <DonutChart data={catChart} labels={catLabels} />
          ) : (
            <p className="dash-empty">{d.empty}</p>
          )}
        </section>

        <section className="dash-panel">
          <h2><UserCheck size={16} /> Rapport des professionnels</h2>
          <div className="dash-kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', marginBottom: 14 }}>
            {trKpis.map((k) => (
              <div key={k.label} className="dash-kpi">
                <em>{k.label}</em>
                <strong style={{ fontSize: '1.4rem' }}>{k.value}</strong>
              </div>
            ))}
          </div>
          {topTranslators.length > 0 && (
            <>
              <h3 style={{ fontSize: '0.85rem', color: 'var(--dash-muted, var(--gray-500))', margin: '0 0 8px' }}>Top professionnels (commandes)</h3>
              <HBarChart data={topTranslators} />
            </>
          )}
        </section>
      </div>

      <section className="dash-panel">
        <h2><Users size={16} /> Rapport clients</h2>
        <div className="dash-kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', marginBottom: 14 }}>
          {clientKpis.map((k) => (
            <div key={k.label} className="dash-kpi">
              <em>{k.label}</em>
              <strong style={{ fontSize: '1.4rem' }}>{k.value}</strong>
            </div>
          ))}
        </div>
        {topClients.length > 0 && (
          <>
            <h3 style={{ fontSize: '0.85rem', color: 'var(--dash-muted, var(--gray-500))', margin: '0 0 8px' }}>Clients avec le plus de commandes</h3>
            <HBarChart data={topClients} />
          </>
        )}
      </section>
    </>
  );
}
